/**
 * Envelope normalization + wire-query builders (ported from cpanel
 * `features/merchants/api/merchantResponse.ts`,
 * `integrationTokens.endpoints.ts`, `merchants.queries.ts` — logic 1:1).
 */
import {
  merchantListSchema,
  merchantSchema,
  type Merchant,
  type MerchantList,
  type IntegrationTokenList,
  type IntegrationTokenVendor,
} from '../model/schema';
import { DEFAULT_MERCHANT_SEARCH_TYPE } from './merchant-labels';

/**
 * List params for `GET /api/v1/admin/merchants/filter` — the only query
 * params the endpoint accepts are `search_type`, `search_value`, `status`,
 * `limit` and `offset`. There is no vertical filter. `status` is
 * `active` | `suspended` | `archived`; omitted — everything except archived.
 */
export interface MerchantListParams {
  search_type?: string | undefined;
  search_value?: string | undefined;
  status?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export interface IntegrationTokenListParams {
  scope?: string | undefined;
  status?: string | undefined;
  /** Vendor phone filter (URL param `phone`; sent as wire `search`). */
  phone?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) ? (value as string[]) : undefined;
}

/**
 * Adapts the API's split `data` + `list` detail envelope into the domain
 * model. The filter list returns flat rows with `null` for unset scalars
 * (`admin_phone: null`) — `null` is normalized to `undefined` first so one
 * null field can't sink the whole row (schema rule: responses render
 * tolerantly, never crash the list).
 */
export function toMerchant(body: unknown): Merchant | undefined {
  if (!isRecord(body)) return undefined;
  const data = isRecord(body.data) ? body.data : body;
  const dataRecord: Record<string, unknown> = data;
  const rawId = dataRecord.merchant_id ?? dataRecord.id;
  const verticals = asStringArray(body.list) ?? asStringArray(dataRecord.verticals);

  const parsed = merchantSchema.safeParse(
    Object.fromEntries(
      Object.entries(dataRecord).map(([key, value]) => [
        key,
        value === null ? undefined : value,
      ]),
    ),
  );
  if (!parsed.success) return undefined;
  return {
    ...parsed.data,
    merchant_id: typeof rawId === 'string' ? rawId : parsed.data.merchant_id,
    ...(verticals ? { verticals } : {}),
  };
}

export function toMerchantList(body: unknown): MerchantList {
  if (!isRecord(body)) return { list: [] };
  const source = Array.isArray(body.list)
    ? body.list
    : Array.isArray(body.data)
      ? body.data
      : [];
  const list = (source as unknown[])
    .map(toMerchant)
    .filter((m): m is Merchant => Boolean(m));
  const parsed = merchantListSchema.safeParse({
    list,
    meta: isRecord(body.meta) ? body.meta : undefined,
  });
  if (!parsed.success) return { list };
  return parsed.data;
}

/**
 * Page count for the table pagination: prefer the backend's own `page_count`,
 * fall back to `ceil(total / limit)`.
 */
export function toMerchantPageCount(
  response: MerchantList | undefined,
  limit: number,
): number {
  const pageCount = Number(response?.meta?.page_count);
  if (Number.isFinite(pageCount) && pageCount >= 0) return pageCount;

  const total = Number(response?.meta?.total) || 0;
  if (!Number.isFinite(limit) || limit <= 0) return 0;
  return Math.ceil(total / limit);
}

export function toMerchantTotal(response: MerchantList | undefined): number {
  const total = Number(response?.meta?.total);
  if (Number.isFinite(total) && total >= 0) return total;
  return response?.list?.length ?? 0;
}

/**
 * Wire query for the token list endpoint. The Core API list endpoint paginates with
 * `limit` + `skip`: the shared table shell keeps `limit` / `offset` in the
 * URL, and the offset is renamed to `skip` at this boundary. Undefined values
 * are dropped so nothing empty is sent.
 */
export function buildIntegrationTokenListQuery(
  params: IntegrationTokenListParams,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (params.scope) out.scope = params.scope;
  if (params.status) out.status = params.status;
  // The wire field stays `search` (backend contract); only the URL param was
  // renamed to `phone`.
  if (params.phone) out.search = params.phone;
  if (params.limit !== undefined) out.limit = params.limit;
  if (params.offset !== undefined) out.skip = params.offset;
  return out;
}

export function buildMerchantListQuery(
  params: MerchantListParams,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  // Typed search rides the wire as-is; a lone type with no value sends
  // nothing, and a value without a type falls back to the default column.
  if (params.search_value) {
    out.search_type = params.search_type ?? DEFAULT_MERCHANT_SEARCH_TYPE;
    out.search_value = params.search_value;
  }
  // Only the backend-known statuses ride the wire; anything else (a stale
  // bookmark, a hand-made link) is dropped so the call stays valid — the
  // backend then returns everything except archived.
  if (
    params.status === 'active' ||
    params.status === 'suspended' ||
    params.status === 'archived'
  ) {
    out.status = params.status;
  }
  if (params.limit !== undefined) out.limit = params.limit;
  if (params.offset !== undefined) out.offset = params.offset;
  return out;
}

export function toIntegrationTokenPageCount(
  vendors: IntegrationTokenList['vendors'] | undefined,
  limit: number,
): number {
  const lastPage = Number(vendors?.last_page);
  if (Number.isFinite(lastPage) && lastPage >= 0) return lastPage;
  const total = Number(vendors?.count) || 0;
  if (!Number.isFinite(limit) || limit <= 0) return 0;
  return Math.ceil(total / limit);
}

export function toIntegrationTokenRows(
  response: IntegrationTokenList | undefined,
): IntegrationTokenVendor[] {
  return response?.vendors?.data ?? [];
}

const MERCHANT_ERROR_MESSAGES: Record<string, string> = {
  '4205:provider_credentials_rejected':
    'У мерчанта нет интеграции, из которой можно импортировать данные.',
  provider_credentials_rejected:
    'У мерчанта нет интеграции, из которой можно импортировать данные.',
};

interface AxiosErrorLike {
  response?: {
    status?: number;
    data?: {
      error?: {
        code?: number | string;
        message?: string;
        details?: { field?: string; issue?: string }[];
      };
      message?: string;
    };
  };
}

interface ApiErrorShape {
  status?: number;
  code?: string | number;
  details?: unknown;
  message?: string;
}

/**
 * Surfaces the service's own message where it has one — field-level validation
 * (invalid INN length, a role mismatch on attach) is far more useful than a
 * generic failure string.
 *
 * Accepts both the legacy axios shape (`error.response.data.error`) and the
 * new `ApiError` shape (`{status, code, details, message}`) — the http client
 * normalizes the same `{error:{code,message,details}}` envelope into both.
 */
export function merchantErrorMessage(err: unknown, fallback: string): string {
  const axiosRes = (err as AxiosErrorLike)?.response;
  const axiosBody = axiosRes?.data?.error;

  const shape = err as ApiErrorShape;
  const status =
    axiosRes?.status ?? (typeof shape?.status === 'number' ? shape.status : undefined);
  const code = axiosBody?.code ?? shape?.code;
  const rawDetails = axiosBody?.details ?? shape?.details;
  const detail = Array.isArray(rawDetails)
    ? (rawDetails[0] as { field?: string; issue?: string } | undefined)
    : undefined;
  const message =
    axiosBody?.message ??
    (typeof shape?.message === 'string' ? shape.message : undefined) ??
    axiosRes?.data?.message;

  const localizedMessage = detail?.issue
    ? (MERCHANT_ERROR_MESSAGES[`${code ?? ''}:${detail.issue}`] ??
      MERCHANT_ERROR_MESSAGES[detail.issue])
    : undefined;
  if (localizedMessage) return localizedMessage;
  if (detail?.field && detail?.issue) return `${detail.field}: ${detail.issue}`;
  if (status === 403) return 'Недостаточно прав для этого действия.';
  if (message === undefined || message === '') return fallback;
  return message;
}
